import type { HTMLAttributes, ReactNode } from 'react'

import { cn } from '@/lib/cn'

/* Base UI (`@base-ui/react`): no primitive. A card is layout and elevation — no state, no
   keyboard semantics, nothing headless to inherit. */

/* Surfaces are separated by hairlines and tone, not by shadow. Cards carry no
   shadow at any time, including on hover — a dense interface with many bordered
   panels turns into noise the moment shadows are added. The single 1px xs
   shadow below is the hairline lift the mockup uses and nothing more. */

export function Card({
  className,
  ...props
}: HTMLAttributes<HTMLDivElement>) {
  return (
    <section
      className={cn(
        // min-w-0 so a wide child (a dense table) scrolls inside its own
        // overflow-x-auto wrapper instead of widening the card and scrolling
        // the whole page sideways — a flex item's default min-width is its
        // content width, which silently defeats every inner scroll container.
        'bg-surface border-border-subtle flex min-w-0 flex-col overflow-hidden',
        'rounded-[var(--radius-DEFAULT)] border shadow-xs',
        className,
      )}
      {...props}
    />
  )
}

export interface CardHeaderProps extends HTMLAttributes<HTMLDivElement> {
  /** Trailing slot — a link, a menu, a segmented control. */
  action?: ReactNode
}

export function CardHeader({
  className,
  children,
  action,
  ...props
}: CardHeaderProps) {
  return (
    <div
      className={cn(
        'flex items-start justify-between gap-2 px-[18px] pt-4',
        className,
      )}
      {...props}
    >
      <div className="flex min-w-0 flex-col gap-1.5">{children}</div>
      {action ? <div className="shrink-0 pt-0.5">{action}</div> : null}
    </div>
  )
}

export function CardTitle({
  className,
  ...props
}: HTMLAttributes<HTMLHeadingElement>) {
  return (
    <h2
      className={cn(
        'text-text-heading text-[15px] leading-tight font-normal tracking-[-0.2px]',
        className,
      )}
      {...props}
    />
  )
}

/** One sentence saying what the surface is FOR, not what it contains. */
export function CardSubtitle({
  className,
  ...props
}: HTMLAttributes<HTMLParagraphElement>) {
  return (
    <p
      className={cn('text-text-muted text-[12.5px] leading-snug font-light', className)}
      {...props}
    />
  )
}

export function CardBody({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('px-[18px] py-4', className)} {...props} />
}

export function CardFooter({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn(
        'border-neutral-100 mt-auto flex items-center gap-2 border-t px-[18px] py-3',
        className,
      )}
      {...props}
    />
  )
}
